import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CursorGlow } from "@/components/CursorGlow";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ClientDashboard from "@/pages/ClientDashboard";
import StaffDashboard from "@/pages/StaffDashboard";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DynamicManifest from "@/components/DynamicManifest";
import ConsentPage from "@/pages/ConsentPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import CookiePage from "@/pages/legal/CookiePage";
import TermsPage from "@/pages/legal/TermsPage";
import AiPage from "@/pages/legal/AiPage";
import LegalNoticePage from "@/pages/legal/LegalNoticePage";

// Redirect logged-in tenant users away from /:slug/login and /:slug/register.
// Chi è collegata a un ALTRO centro vede invece la pagina di questo centro
// (con l'avviso "sei collegata a …"): prima finiva nell'area del centro
// sbagliato, con ogni schermata in errore.
const TenantPublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  const { slug } = useParams();
  if (loading) return null;
  const sameTenant = !user?.tenant_slug || user.tenant_slug === String(slug || "").toLowerCase();
  if (user && user.role !== "super_admin" && sameTenant) {
    const target = user.role === "client" ? `/${slug}/app` : `/${slug}/staff`;
    return <Navigate to={target} replace />;
  }
  return children;
};

// Redirect logged-in super admin away from /admin/login.
const AdminPublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === "super_admin") return <Navigate to="/admin" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="bg-[#FAFAFA] text-[#1A1A1A] antialiased" data-testid="app-root">
          {/* Manifest PWA per centro: "Aggiungi alla Home" salva il salone, non la radice */}
          <DynamicManifest />
          <Routes>
            {/* Public landing */}
            <Route path="/" element={<LandingPage />} />

            {/* Modulo consensi GDPR aperto dalla cliente col QR del centro */}
            <Route path="/consenso/:token" element={<ConsentPage />} />
            {/* Disiscrizione dalle promozioni: link in fondo alle email del centro */}
            <Route path="/:slug/disiscriviti/:token" element={<UnsubscribePage />} />

            {/* Documenti legali — devono restare raggiungibili senza login */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookie-policy" element={<CookiePage />} />
            <Route path="/termini" element={<TermsPage />} />
            <Route path="/intelligenza-artificiale" element={<AiPage />} />
            <Route path="/note-legali" element={<LegalNoticePage />} />

            {/* Super-admin (no tenant slug) */}
            <Route
              path="/admin/login"
              element={
                <AdminPublicOnly>
                  <AdminLoginPage />
                </AdminPublicOnly>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute roles={["super_admin"]} fallback="/admin/login">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Tenant-scoped routes (slug = centro) */}
            <Route
              path="/:slug/login"
              element={
                <TenantPublicOnly>
                  <LoginPage />
                </TenantPublicOnly>
              }
            />
            <Route
              path="/:slug/register"
              element={
                <TenantPublicOnly>
                  <RegisterPage />
                </TenantPublicOnly>
              }
            />
            <Route path="/:slug/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/:slug/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/:slug/app/*"
              element={
                <ProtectedRoute roles={["client"]}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:slug/staff/*"
              element={
                <ProtectedRoute roles={["staff", "admin"]}>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CursorGlow />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
